import React from 'react';
import { Box, Container, Typography, Card, CardContent } from '@mui/material';
import { motion } from 'framer-motion';
import CancelIcon from '@mui/icons-material/Cancel';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';

const problemCards = [
  {
    icon: <ThumbDownIcon sx={{ fontSize: 50 }} />,
    title: 'Потеря дохода',
    description: 'Каждый игрок, который уходит из игры - это потерянный навсегда доход. 80% игроков покидают игру сразу после обнаружения читеров.',
  },
  {
    icon: <CancelIcon sx={{ fontSize: 50 }} />,
    title: 'Испорченная репутация',
    description: 'Информация об играх с проблемами читерства быстро распространяется, отталкивая потенциальных игроков и нанося ущерб вашему бренду.',
  },
  {
    icon: <MoneyOffIcon sx={{ fontSize: 50 }} />,
    title: 'Потраченный маркетинг',
    description: 'Ваши затраты на привлечение пользователей пропадают впустую, когда игроки уходят из-за читеров.',
  },
];

// Animation variants for staggered animations
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.2,
    },
  },
};

const cardVariants = {
  hidden: { y: 50, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.6,
      ease: 'easeOut',
    },
  },
};

const ProblemSection: React.FC = () => {
  return (
    <Box
      id="problem"
      sx={{
        py: 12,
        background: '#f8f9fa',
      }}
    >
      <Container>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h2"
              component="h2"
              color="primary"
              sx={{ fontWeight: 700, mb: 2 }}
            >
              Проблема: Читеры убивают вашу игру
            </Typography>
            <Typography variant="h5" sx={{ color: 'text.secondary', maxWidth: '800px', mx: 'auto' }}>
              В современном игровом ландшафте, где более 1,5 миллиарда людей играют в онлайн-игры по всему миру,
              читерство стало массовой проблемой, которая напрямую влияет на ваш доход.
            </Typography>
          </motion.div>
        </Box>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 4,
              justifyContent: 'center',
            }}
          >
            {problemCards.map((card, index) => (
              <motion.div key={index} variants={cardVariants} style={{ flex: 1 }}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    borderRadius: 4,
                    border: '1px solid rgba(0,0,0,0.08)',
                    background: 'rgba(255,255,255,0.8)',
                    backdropFilter: 'blur(10px)',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      p: 4,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(211, 47, 47, 0.1)',
                        color: 'error.main',
                        mb: 3,
                      }}
                    >
                      {card.icon}
                    </Box>
                    <Typography variant="h4" component="h3" sx={{ mb: 2, fontWeight: 600 }}>
                      {card.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {card.description}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </Box>
        </motion.div>

        <Box sx={{ mt: 10, p: 5, backgroundColor: 'rgba(211, 47, 47, 0.05)', borderRadius: 4 }}>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h4" sx={{ mb: 2, fontWeight: 600, color: 'error.main' }}>
              Традиционные методы обнаружения читеров дороги и неэффективны
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Ручная проверка подозрительного игрового процесса обходится разработчикам примерно в 500 рублей за случай.
              Для игры среднего размера с 10 000 игроков это 300 проверок ежемесячно — а это непосильные 150 000 рублей операционных расходов.
            </Typography>
            <Typography variant="h6" sx={{ color: 'error.main', fontWeight: 600 }}>
              Результат? Для малых и средних студий 40% потенциальных случаев читерства остаются полностью непроверенными.
            </Typography>
          </motion.div>
        </Box>
      </Container>
    </Box>
  );
};

export default ProblemSection; 